import React, { useState } from 'react'

import {
  Box,
  Spinner,
  colors,
  useUnsafeCallback,
  useUnsafeMemo,
} from '@factorialco/deprecated-design-system'
import { Layer, Point, ResponsiveLine } from '@nivo/line'
import { FinancialWorkspaceValueObjectsPeriodEnum } from 'generated/resources/graphql'
import { useMediaQuery } from 'usehooks-ts'

import { ChartData } from 'modules/finance/components/FinancialWorkspace/components/Table/types'

import { CustomDashedLineLayer } from './CustomDashedLineLayer'
import { CustomHoverLayer } from './CustomHoverLayer'
import { CustomVerticalLineLayer } from './CustomVerticalLineLayer'
import { Tick } from './Tick'
import { Tooltip } from './Tooltip'
import { CentToFormattedAmount } from './helpers'
import { generateSeries, highestDataPoint, isEmpty, parseChartData, theme } from './helpers'

interface ChartProps {
  loading: boolean
  chartData?: ChartData[]
  period?: FinancialWorkspaceValueObjectsPeriodEnum
  toAmount: (value: number) => CentToFormattedAmount
}

export const LineChart: React.FC<ChartProps> = ({ period, loading, toAmount, chartData }) => {
  const [hoveredPoint, setHoveredPoint] = useState<Point | null>(null)
  const isSmallScreen = useMediaQuery('(max-width: 575px)')

  const parsedChartData = useUnsafeMemo(() => parseChartData(chartData), [chartData])

  const noData = useUnsafeMemo(() => isEmpty(parsedChartData), [parsedChartData])
  const series = useUnsafeMemo(() => generateSeries(parsedChartData), [parsedChartData])

  const layers: Layer[] = useUnsafeMemo(
    () => [
      'grid',
      'lines',
      CustomDashedLineLayer,
      'markers',
      'axes',
      'areas',
      CustomVerticalLineLayer(hoveredPoint, series),
      CustomHoverLayer(hoveredPoint, chartData, series),
      'points',
      'slices',
      'mesh',
      'legends',
    ],
    [hoveredPoint, chartData, series]
  )

  const highestYValue = useUnsafeMemo(() => {
    return highestDataPoint(parsedChartData)?.y || 0
  }, [parsedChartData])

  const upTo10K = highestYValue >= 1000000

  const formatLeftAxis = useUnsafeCallback(
    (value: number) => {
      if (value !== 0 && noData) return ''
      return upTo10K ? toAmount(value).formattedAmount : `${toAmount(value).amount}`.split('.')[0]
    },
    [upTo10K, toAmount, noData]
  )

  if (loading) {
    return (
      <Box padding="s40" width="full" alignItems="center" flexGrow>
        <Spinner />
      </Box>
    )
  }

  return (
    <ResponsiveLine
      useMesh
      data={series}
      theme={theme}
      layers={layers}
      enableGridX={false}
      enablePoints={false}
      margin={{
        right: upTo10K ? 2 : 35,
        top: chartData?.length ? 10 : 0,
        bottom: chartData?.length ? 40 : 20,
        left: noData ? 32 : upTo10K ? 44 : 51,
      }}
      onMouseLeave={() => setHoveredPoint(null)}
      onMouseMove={(point) => setHoveredPoint(point)}
      tooltip={Tooltip(series, chartData || [], toAmount)}
      markers={[{ axis: 'y', value: 0, lineStyle: { stroke: colors.grey600 } }]}
      axisLeft={{ tickSize: 0, tickValues: 4, tickPadding: 14, format: formatLeftAxis }}
      yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
      axisBottom={{
        tickSize: 2,
        tickPadding: 5,
        tickRotation: 0,
        renderTick: Tick(period, isSmallScreen),
      }}
      colors={({ id }) => {
        const category = (chartData || []).find((obj) => obj.serieId === id)
        return category?.pointColor || colors.grey600
      }}
    />
  )
}
